import { z } from 'zod';
import { COUNTRY_CODES } from './countries.js';
import { USER_GROUPS } from './group.js';
export const ActivityStatus = {
    /** Visible only to its author. */
    DRAFT: 'DRAFT',
    /** Visible to leadership / dashboard. */
    PUBLISHED: 'PUBLISHED',
    /** Was planned but will not happen; kept for the record. */
    CANCELLED: 'CANCELLED',
};
/**
 * Where an activity is in its life, derived from the stored status, `endsAt` and `reportedAt`
 * (never stored, so "awaiting report" begins by itself when the activity ends):
 *
 *   DRAFT → PLANNED → (ends) → AWAITING_REPORT → (report filed) → REALISED
 *                  ↘ CANCELLED (with a reason, before or instead of the report)
 *
 * An activity entered after it happened, with its report, is REALISED at once.
 * See docs/05-data-model.md#activity-lifecycle.
 */
export const ActivityStage = {
    DRAFT: 'DRAFT',
    PLANNED: 'PLANNED',
    AWAITING_REPORT: 'AWAITING_REPORT',
    REALISED: 'REALISED',
    CANCELLED: 'CANCELLED',
};
export function activityStage(a, now = new Date()) {
    if (a.status === ActivityStatus.DRAFT)
        return ActivityStage.DRAFT;
    if (a.status === ActivityStatus.CANCELLED)
        return ActivityStage.CANCELLED;
    if (a.reportedAt)
        return ActivityStage.REALISED;
    return new Date(a.endsAt).getTime() > now.getTime()
        ? ActivityStage.PLANNED
        : ActivityStage.AWAITING_REPORT;
}
export const MediaKind = {
    IMAGE: 'IMAGE',
    VIDEO: 'VIDEO',
    /** PDF, Word, Excel or OpenDocument file, stored exactly as uploaded. */
    DOCUMENT: 'DOCUMENT',
};
export const MediaStatus = {
    PROCESSING: 'PROCESSING',
    READY: 'READY',
    FAILED: 'FAILED',
};
/** Profile pictures are always stored square, at this size (pixels). */
export const AVATAR_SIZE = 512;
export const AVATAR_MAX_BYTES = 10 * 1024 * 1024;
export const MEDIA_LIMITS = {
    IMAGE_MAX_BYTES: 15 * 1024 * 1024,
    VIDEO_MAX_BYTES: 100 * 1024 * 1024,
    MAX_FILES_PER_ACTIVITY: 30,
    IMAGE_MIME_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
    VIDEO_MIME_TYPES: ['video/mp4', 'video/quicktime', 'video/webm'],
    DOCUMENT_MAX_BYTES: 25 * 1024 * 1024,
    /** Recognised by content, not by name (see apps/api/src/modules/media/document-type.ts). */
    DOCUMENT_EXTENSIONS: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'odt', 'ods'],
};
/** At most this many concerns heard from citizens per activity. */
export const MAX_CONCERNS = 3;
const endsAfterStart = {
    message: 'Mbarimi duhet të jetë pas fillimit.',
    path: ['endsAt'],
};
const pressLink = z
    .string()
    .trim()
    .max(1000)
    .regex(/^https?:\/\/\S+$/i, 'Lidhja duhet të fillojë me http:// ose https://.');
const countryCode = z
    .string()
    .refine((c) => COUNTRY_CODES.includes(c), 'Zgjidhni shtetin nga lista.');
/** One of the (up to three) main concerns citizens raised during the activity. */
export const concernSchema = z.object({
    topicId: z.string().uuid('Zgjidhni temën e shqetësimit.'),
    /** What exactly people said, in a sentence. */
    text: z.string().trim().max(300).optional(),
});
/**
 * Field rules of the plan, shared by create and update. No defaults here: defaults belong only
 * to create, otherwise a partial update would silently reset omitted fields.
 */
const planFields = {
    title: z.string().trim().min(3, 'Titulli duhet të ketë të paktën 3 shkronja.').max(200),
    categoryIds: z
        .array(z.string().uuid())
        .min(1, 'Zgjidhni të paktën një kategori.')
        .max(10, 'Mund të zgjidhni deri në 10 kategori.'),
    /** TipTap / ProseMirror JSON document — the editable source of truth. */
    content: z.record(z.string(), z.unknown()),
    /** HTML rendering of `content`, produced by the editor. Sanitised again server-side. */
    contentHtml: z.string().max(500_000),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    // In Albania: county + municipality (+ branch). Abroad: country + city.
    countyId: z.string().uuid(),
    municipalityId: z.string().uuid(),
    unitId: z.string().uuid(),
    countryCode,
    city: z.string().trim().min(2, 'Shkruani qytetin.').max(100),
    locationDetail: z.string().trim().max(300),
    /** Guests who are not registered users (free text). */
    speakers: z.array(z.string().trim().min(1).max(150)).max(50),
    /** Registered users tagged as speakers or participants. */
    taggedUserIds: z.array(z.string().uuid()).max(50),
    targetGroup: z.string().trim().max(300),
    /** Albanian lek (ALL). No longer asked in the form; kept for older records. */
    budget: z
        .number()
        .int('Shkruani buxhetin si numër të plotë, pa presje.')
        .min(0)
        .max(10_000_000_000),
    /** The political message of the week this activity carries (optional). */
    messageId: z.string().uuid(),
};
const pressLinks = z.array(pressLink).max(20);
const mediaIds = z.array(z.string().uuid()).max(MEDIA_LIMITS.MAX_FILES_PER_ACTIVITY);
/**
 * The report, filed after the activity: what happened. PUT semantics (the whole report is
 * replaced), so the lists default to empty.
 */
export const activityReportSchema = z.object({
    participantsCount: z.number().int().min(0).max(1_000_000).optional(),
    outcome: z.string().trim().min(3, 'Shkruani shkurt si shkoi dhe çfarë u arrit.').max(5000),
    concerns: z
        .array(concernSchema)
        .max(MAX_CONCERNS, `Shënoni deri në ${MAX_CONCERNS} shqetësime.`)
        .default([]),
    pressLinks: pressLinks.default([]),
    mediaIds: mediaIds.default([]),
});
const f = planFields;
export const activityCreateSchema = z
    .object({
    title: f.title,
    categoryIds: f.categoryIds,
    content: f.content,
    contentHtml: f.contentHtml,
    startsAt: f.startsAt,
    endsAt: f.endsAt,
    countyId: f.countyId.optional(),
    municipalityId: f.municipalityId.optional(),
    unitId: f.unitId.optional(),
    countryCode: f.countryCode.optional(),
    city: f.city.optional(),
    locationDetail: f.locationDetail.optional(),
    speakers: f.speakers.default([]),
    taggedUserIds: f.taggedUserIds.default([]),
    targetGroup: f.targetGroup.optional(),
    budget: f.budget.optional(),
    messageId: f.messageId.optional(),
    /** Only for an activity entered after it took place: it is then realised at once. */
    report: activityReportSchema.optional(),
    status: z.enum([ActivityStatus.DRAFT, ActivityStatus.PUBLISHED]).default(ActivityStatus.DRAFT),
})
    .refine((a) => a.endsAt >= a.startsAt, endsAfterStart);
/**
 * PATCH of the plan: omitted = unchanged; `null` clears an optional field. The report has its
 * own endpoint, and so has cancelling. Never add `.default()` here (see activity.test.ts).
 */
export const activityUpdateSchema = z
    .object({
    title: f.title.optional(),
    categoryIds: f.categoryIds.optional(),
    content: f.content.optional(),
    contentHtml: f.contentHtml.optional(),
    startsAt: f.startsAt.optional(),
    endsAt: f.endsAt.optional(),
    countyId: f.countyId.nullable().optional(),
    municipalityId: f.municipalityId.nullable().optional(),
    unitId: f.unitId.nullable().optional(),
    countryCode: f.countryCode.nullable().optional(),
    city: f.city.nullable().optional(),
    locationDetail: f.locationDetail.nullable().optional(),
    speakers: f.speakers.optional(),
    taggedUserIds: f.taggedUserIds.optional(),
    targetGroup: f.targetGroup.nullable().optional(),
    budget: f.budget.nullable().optional(),
    messageId: f.messageId.nullable().optional(),
    /** Publish a draft, or take a planned activity back to draft. */
    status: z.enum([ActivityStatus.DRAFT, ActivityStatus.PUBLISHED]).optional(),
})
    .refine((a) => !a.startsAt || !a.endsAt || a.endsAt >= a.startsAt, endsAfterStart);
/** Cancelling keeps the activity on record, with the reason: cancellations are data too. */
export const activityCancelSchema = z.object({
    reason: z
        .string()
        .trim()
        .min(3, 'Shkruani shkurt arsyen e anulimit.')
        .max(300, 'Arsyeja duhet të jetë e shkurtër, deri në 300 shkronja.'),
});
export const activityQuerySchema = z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    countyId: z.string().uuid().optional(),
    municipalityId: z.string().uuid().optional(),
    unitId: z.string().uuid().optional(),
    /** Activities abroad in this country. */
    countryCode: z.string().length(2).optional(),
    /** Activities having this category (among possibly others). */
    categoryId: z.string().uuid().optional(),
    authorId: z.string().uuid().optional(),
    /** Activities whose author belongs to this group. */
    group: z.enum(USER_GROUPS).optional(),
    /** Activities this person organised or was tagged in. */
    personId: z.string().uuid().optional(),
    messageId: z.string().uuid().optional(),
    stage: z.enum(ActivityStage).optional(),
    q: z.string().trim().max(200).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
//# sourceMappingURL=activity.js.map